import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from 'react-i18next'
import { PGET } from 'helpers/api'
import { formatMoney } from 'helpers/helpers'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { DebounceInput } from "react-debounce-input"
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";

// Demo POSes are filtered server-side by PosDAOImpl.DEMO_POS_IDS so the
// backend never returns them here. The old hardcoded TEST_ACCOUNT_IDS was
// dropped — update the Java constant to add/remove demo ids.
function Index() {
	const { t } = useTranslation()
	const [data, setData] = useState([])        // visible (filtered)
	const [allData, setAllData] = useState([])  // full original
	const [filter, setFilter] = useState('')
	const [startDate, setStartDate] = useState(null) // Date format
	const [endDate, setEndDate] = useState(null)

	useEffect(() => {
		getData()
	}, [])

	async function getData() {
		const response = await PGET('services/admin/api/get-payments-by-months', {}, { loader: true })
		if (response) {
			setAllData(response.data || [])
			setData(response.data || [])
		}
	}

	function search(searchValue) {
		setFilter(searchValue)
	}

	// Collect month keys from any of the available maps
	const allMonthKeys = useMemo(() => {
		const set = new Set()
		allData.forEach(row => {
			Object.keys(row.forThisMonth || {}).forEach(k => set.add(k))
			Object.keys(row.forAndInSameMonth || {}).forEach(k => set.add(k))
			Object.keys(row.paidInThisMonth || {}).forEach(k => set.add(k))
			// backward-compat if API still returns monthlyAmounts
			Object.keys(row.monthlyAmounts || {}).forEach(k => set.add(k))
		})
		return Array.from(set).sort() // 'YYYY-MM' lexicographically sorts chronologically
	}, [allData])

	const visibleMonths = useMemo(() => {
		if (!startDate && !endDate) return allMonthKeys
		const startKey = startDate ? format(startDate, "yyyy-MM") : allMonthKeys[0]
		const endKey = endDate ? format(endDate, "yyyy-MM") : allMonthKeys[allMonthKeys.length - 1]
		return allMonthKeys.filter(k => k >= startKey && k <= endKey)
	}, [startDate, endDate, allMonthKeys])

	// Which field to display in the main table cells.
	// We show the "Paid FOR this month (regardless WHEN paid)" metric.
	const fieldToShow = 'forThisMonth'

	function downloadExcel() {
		const header = ["ID", "месяц подключения", "Логины", ...visibleMonths.map(formatMonthLabel)]
		const body = regularData.map(row => [
			row.posId,
			row.createdDate,
			row.login,
			...visibleMonths.map(k => {
				const accrued = Number(row[fieldToShow]?.[k] ?? row.monthlyAmounts?.[k] ?? 0)
				if (accrued > 0) return accrued
				if ((row.bonusMonths || []).includes(k)) return t('BONUS')
				const cash = Number(row.paidInThisMonth?.[k] ?? 0)
				return cash > 0 ? cash : 0
			})
		])
		const ws = XLSX.utils.aoa_to_sheet([header, ...body])
		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, "Report")
		const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" })
		const file = new Blob([excelBuffer], { type: "application/octet-stream" })
		saveAs(file, "report.xlsx")
	}

	function sumByField(rows, monthKeys, fieldName) {
		if (!rows.length) return {}
		const totals = {}
		monthKeys.forEach(mk => {
			totals[mk] = rows.reduce((sum, r) => {
				const map = r[fieldName] || {}
				// fallback to legacy monthlyAmounts ONLY for forThisMonth display
				const val = fieldName === 'forThisMonth'
					? (map[mk] ?? r.monthlyAmounts?.[mk] ?? 0)
					: (map[mk] ?? 0)
				return sum + (Number(val) || 0)
			}, 0)
		})
		return totals
	}

	const visibleData = useMemo(() => {
		if (!filter) return allData
		const q = filter.toLowerCase()
		return allData.filter(item =>
			`${item.posId}`.includes(q) ||
			`${item.login}`.toLowerCase().includes(q)
		)
	}, [allData, filter])

	// Backend filters DEMO_POS_IDS server-side, so visibleData is already clean.
	const regularData = visibleData

	// 4 summary rows — demo POSes are excluded upstream by the SQL filter.
	const totalsForThisMonth = useMemo(() => sumByField(regularData, visibleMonths, 'forThisMonth'), [regularData, visibleMonths])
	const totalsForAndInSameMonth = useMemo(() => sumByField(regularData, visibleMonths, 'forAndInSameMonth'), [regularData, visibleMonths])
	const totalsPaidInThisMonth = useMemo(() => sumByField(regularData, visibleMonths, 'paidInThisMonth'), [regularData, visibleMonths])

	const searchRef = useRef(null)
	useEffect(() => {
		const handleKeyPress = (e) => {
			const active = document.activeElement
			const isEditable =
				active &&
				(active.tagName === "INPUT" || active.tagName === "TEXTAREA") &&
				!active.disabled &&
				!active.readOnly
			if (!isEditable && searchRef.current) {
				searchRef.current.focus()
			}
		}
		document.addEventListener("keydown", handleKeyPress)
		return () => document.removeEventListener("keydown", handleKeyPress)
	}, [])

	return (
		<>
			<style>
				{`
  /* === Scope + geometry === */
  .report-wrap { 
    --rowh: 36px;          /* footer row height */
    --w1: 250px;           /* ID col width */
    --w2: 150px;           /* Login col width */
    --w3: 150px;           /* Created date col width */
  }

  /* sticky left columns (shared for thead/tbody/tfoot) */
  .sticky-col-1 { position: sticky; left: 0;                          min-width: var(--w1); }
  .sticky-col-2 { position: sticky; left: var(--w1);                  min-width: var(--w2); }
  .sticky-col-3 { position: sticky; left: calc(var(--w1) + var(--w2)); min-width: var(--w3); }

  /* table grid & borders */
  table.report-grid { border-collapse: separate; border-spacing: 0; width: 100%; }
  table.report-grid th, table.report-grid td { border: 1px solid #dee2e6; }

  /* THEAD: sticky + light gray */
  .report-wrap thead { position: sticky; top: 0; z-index: 4; background: #f8f9fa; }
  .report-wrap thead th { background: #f8f9fa; border-bottom: 2px solid #000 !important; }
  /* keep left sticky cells in head above body cells */
  .report-wrap thead th.sticky-col-1,
  .report-wrap thead th.sticky-col-2,
  .report-wrap thead th.sticky-col-3 { z-index: 8; background: #f8f9fa; }

  /* TFOOT: sticky rows stacked at bottom */
  .report-wrap tfoot.tfoot-sticky tr {
    position: sticky;
    background: #f8f9fa;
    z-index: 5;                 /* above tbody */
    height: var(--rowh);
  }
  .report-wrap tfoot.tfoot-sticky tr > td {
    height: var(--rowh);
    padding-top: 8px;
    padding-bottom: 8px;
    border-bottom: 0 !important; /* avoid double borders */
  }
  /* stack offsets — 4 rows */
  .report-wrap tfoot.tfoot-sticky tr.row-4 { bottom: 0; }
  .report-wrap tfoot.tfoot-sticky tr.row-3 { bottom: calc(var(--rowh) * 1); }
  .report-wrap tfoot.tfoot-sticky tr.row-2 { bottom: calc(var(--rowh) * 2); }
  .report-wrap tfoot.tfoot-sticky tr.row-1 { bottom: calc(var(--rowh) * 3); }
  /* separators between footer rows */
  .report-wrap tfoot.tfoot-sticky tr.row-1 td { border-top: 2px solid #000 !important; }
  .report-wrap tfoot.tfoot-sticky tr.row-2 td,
  .report-wrap tfoot.tfoot-sticky tr.row-3 td,
  .report-wrap tfoot.tfoot-sticky tr.row-4 td { border-top: 1px solid #adb5bd !important; }
  /* left sticky cells in footer need background + higher z-index */
  .report-wrap tfoot.tfoot-sticky td.sticky-col-1,
  .report-wrap tfoot.tfoot-sticky td.sticky-col-2,
  .report-wrap tfoot.tfoot-sticky td.sticky-col-3 { z-index: 9; background:#f8f9fa; }

  /* body left sticky cells background */
  .report-wrap tbody td.sticky-col-1,
  .report-wrap tbody td.sticky-col-2,
  .report-wrap tbody td.sticky-col-3 { background:#fff; z-index: 1; }

  /* keep grid lines on sticky left cells */
  .report-wrap thead th.sticky-col-1,
  .report-wrap tbody td.sticky-col-1,
  .report-wrap tfoot td.sticky-col-1 { border-right:1px solid #dee2e6 !important; }

  .report-wrap thead th.sticky-col-2,
  .report-wrap tbody td.sticky-col-2,
  .report-wrap tfoot td.sticky-col-2 { border-right:1px solid #dee2e6 !important; }

  .report-wrap thead th.sticky-col-3,
  .report-wrap tbody td.sticky-col-3,
  .report-wrap tfoot td.sticky-col-3 { border-right:1px solid #dee2e6 !important; }

  /* Test akkauntlar sarlavhasi — hisobga olinmaydi */
  .report-wrap tbody td.test-sep {
    background: #eaecf0 !important;
    color: #667085;
    font-weight: 700;
    text-align: center;
    padding: 10px;
  }

  /* Test akkauntlar qatorlari — xira kulrang fon, hisobga olinmaganini bildiradi */
  .report-wrap tbody tr.test-row td { background: #f2f4f7 !important; color: #98a2b3 !important; }
                `}
			</style>

			<div className="card">
				<div className="card-body">
					<div className="d-flex mb-3 justify-content-between">
						<div className="me-2">
							<div className="position-relative me-2">
								<i className="uil-search input-search-icon"></i>
								<DebounceInput
									inputRef={searchRef}
									type="text"
									className="form-control"
									placeholder={t('SEARCH') + '...'}
									minLength={1}
									debounceTimeout={1000}
									value={filter}
									onChange={(e) => search(e.target.value)}
								/>
							</div>
						</div>

						<div className="d-flex gap-3 mb-3 align-items-end">
							<div>
								<DatePicker
									selected={startDate}
									onChange={(date) => setStartDate(date)}
									dateFormat="MMMM yyyy"
									showMonthYearPicker
									showFullMonthYearPicker
									className="form-control w-50px"
									placeholderText={t('boshlanish oy')}
								/>
							</div>
							<div className="h-100 d-flex justify-content-center align-items-center">-</div>
							<div>
								<DatePicker
									selected={endDate}
									onChange={(date) => setEndDate(date)}
									dateFormat="MMMM yyyy"
									showMonthYearPicker
									showFullMonthYearPicker
									className="form-control w-50px"
									placeholderText={t('tugash oy')}
									minDate={startDate}
								/>
							</div>
						</div>

						<div>
							<button onClick={downloadExcel} className="btn btn-success">
								{t('Excelga yuklash')}
							</button>
						</div>
					</div>
					<div className="report-wrap" style={{ maxHeight: '550px', overflow: 'auto', position: 'relative' }}>
						<table className="table mb-0 report-grid" style={{ maxHeight: '550px', overflow: 'auto', position: 'relative', whiteSpace: 'nowrap', borderCollapse: 'collapse', width: '100%' }}>
							<thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 3, }}>
								<tr>
									<th style={{ backgroundColor: "white", zIndex: 99, }} className="sticky-col-1">{t('ID')}</th>
									<th style={{ backgroundColor: "white", zIndex: 99, }} className="sticky-col-2">{t('Логины')}</th>
									<th style={{ backgroundColor: "white", zIndex: 99, }} className="sticky-col-3">{t('месяц подключения')}</th>
									{visibleMonths.map((key, idx) => (
										<th style={{ backgroundColor: "white", zIndex: 99, }} key={idx} className="text-center">
											{formatMonthLabel(key)}
										</th>
									))}
								</tr>
							</thead>

							<tbody>
								{/* Regular accounts */}
								{regularData.map((item, index) => (
									<tr key={`regular-${index}`}>
										<td className="sticky-col-1">{item.posId}</td>
										<td className="sticky-col-2">{item.login}</td>
										<td className="sticky-col-3">{item.createdDate}</td>
										{visibleMonths.map((key, idx) => {
											const accrued = Number(item[fieldToShow]?.[key] ?? item.monthlyAmounts?.[key] ?? 0)
											const cash = Number(item.paidInThisMonth?.[key] ?? 0)
											const isBonus = (item.bonusMonths || []).includes(key)
											// Cell priority: coverage > bonus > cash-only (gray, no coverage yet) > blank.
											// Cash-only cells make partial / gateway payments visible even when no
											// pos_balance_history row has been written yet.
											let content
											let cellStyle
											if (accrued > 0) {
												content = formatMoney(accrued)
											} else if (isBonus) {
												content = t('BONUS')
											} else if (cash > 0) {
												content = formatMoney(cash)
												cellStyle = { color: '#98a2b3', fontStyle: 'italic' }
											} else {
												content = formatMoney(0)
											}
											return (
												<td key={idx} className="text-center" style={cellStyle}>
													{content}
												</td>
											)
										})}
									</tr>
								))}

							</tbody>

							{/* 4 summary rows — Rows 1/2 = charges, Row 3 = carry-over, Row 4 = cash */}
							<tfoot className="tfoot-sticky">
								<tr className="row-1" style={{
									position: 'sticky',
									bottom: 114,
									zIndex: 2,
									border: '22px'
								}}>
									<td className="sticky-col-1"><strong>{t('Начислено за месяц (независимо от даты оплаты)')}</strong></td>
									<td className="sticky-col-2"></td>
									<td className="sticky-col-3"></td>
									{visibleMonths.map((key, idx) => (
										<td key={idx} className="text-center">{formatMoney(totalsForThisMonth[key] || 0)}</td>
									))}
								</tr>

								<tr className="row-2" style={{
									position: 'sticky',
									bottom: 76,
									zIndex: 2,
									border: '22px'
								}}>
									<td className="sticky-col-1"><strong>{t('Начислено за месяц, оплачено в этом месяце')}</strong></td>
									<td className="sticky-col-2"></td>
									<td className="sticky-col-3"></td>
									{visibleMonths.map((key, idx) => (
										<td key={idx} className="text-center">{formatMoney(totalsForAndInSameMonth[key] || 0)}</td>
									))}
								</tr>

								<tr className="row-3" style={{
									position: 'sticky',
									bottom: 38,
									zIndex: 2,
									border: '22px'
								}}>
									<td className="sticky-col-1"><strong>{t('Начислено за месяц из оплат прошлых месяцев')}</strong></td>
									<td className="sticky-col-2"></td>
									<td className="sticky-col-3"></td>
									{visibleMonths.map((key, idx) => (
										<td key={idx} className="text-center">{formatMoney(totalsForThisMonth[key] - totalsForAndInSameMonth[key])}</td>
									))}
								</tr>

								<tr className="row-4" style={{
									position: 'sticky',
									bottom: 0,
									zIndex: 2,
									border: '22px'
								}}>
									<td className="sticky-col-1"><strong>{t('Поступило в этом месяце')}</strong></td>
									<td className="sticky-col-2"></td>
									<td className="sticky-col-3"></td>
									{visibleMonths.map((key, idx) => (
										<td key={idx} className="text-center">{formatMoney(totalsPaidInThisMonth[key] || 0)}</td>
									))}
								</tr>
							</tfoot>
						</table>
					</div>
				</div>
			</div>
		</>
	)
}

export default Index

function formatMonthLabel(key) {
	const [year, month] = key.split("-")
	const monthNames = [
		'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
		'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'
	]
	return `${monthNames[parseInt(month, 10) - 1]} ${year.slice(2)}`
}